import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { AppState, Guest } from '@/types';

interface Store extends AppState {
  token: string | null;
  setToken: (token: string | null) => void;
  setView: (view: AppState['currentView']) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setReservation: (reservation: AppState['reservation']) => void;
  setProperty: (property: AppState['property']) => void;
  setGuests: (guests: AppState['guests']) => void;
  setCurrentGuestIndex: (index: number) => void;
  updateGuest: (index: number, guest: Partial<Guest>) => void;
  setMaintenanceEnabled: (enabled: boolean) => void;
  setMaintenanceMessage: (message: string) => void;
  nextGuest: () => void;
  prevGuest: () => void;
}

const storeDefinition = (set: (partial: Partial<Store> | ((state: Store) => Partial<Store>)) => void, get: () => Store): Store => ({
  currentView: 'loading',
  reservation: null,
  property: null,
  guests: [],
  currentGuestIndex: 0,
  error: null,
  isLoading: false,
  token: null,
  maintenanceEnabled: false,
  maintenanceMessage: '',

  setToken: (token) => set({ token }),
  setView: (view) => set({ currentView: view }),
  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),
  setReservation: (reservation) => set({ reservation }),
  setProperty: (property) => set({ property }),
  setGuests: (guests) => set({ guests }),
  setCurrentGuestIndex: (index) => set({ currentGuestIndex: index }),
  setMaintenanceEnabled: (enabled) => set({ maintenanceEnabled: enabled }),
  setMaintenanceMessage: (message) => set({ maintenanceMessage: message }),

  updateGuest: (index, updates) =>
    set((state) => {
      const newGuests = [...state.guests];
      if (index >= newGuests.length) {
        for (let i = newGuests.length; i <= index; i++) {
          newGuests[i] = {} as Guest;
        }
      }
      newGuests[index] = { ...(newGuests[index] || {}), ...updates } as Guest;
      return { guests: newGuests };
    }),

  nextGuest: () => {
    const { currentGuestIndex, reservation } = get();
    if (reservation && currentGuestIndex < reservation.guestCount - 1) {
      set({ currentGuestIndex: currentGuestIndex + 1 });
    }
  },

  prevGuest: () => {
    const { currentGuestIndex } = get();
    if (currentGuestIndex > 0) {
      set({ currentGuestIndex: currentGuestIndex - 1 });
    }
  },
});

export const useAppStore = create<Store>()(
  import.meta.env.DEV ? devtools(storeDefinition) : storeDefinition
);
